<script lang="ts">
  // Cut a fragment out of the local recording and download it. The file is
  // fetched by plain navigation so the browser's own download manager takes
  // it — that keeps large clips out of memory and works on phones.
  import { onMount } from 'svelte';
  import { clipState, clipUrl, listArchive } from '../lib/api';

  type Props = {
    streamName: string;
    streamLabel: string;
    onclose: () => void;
  };
  let { streamName, streamLabel, onclose }: Props = $props();

  function pad(n: number): string {
    return String(n).padStart(2, '0');
  }
  function toLocal(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  function fmt(ms: number): string {
    const d = new Date(ms);
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  let startVal = $state('');
  let endVal = $state('');
  let h264 = $state(false);
  let err = $state('');
  let hint = $state('');
  let busy = $state(false);
  let available = $state<{ from: number; to: number; gb: number } | null>(null);
  let loading = $state(true);

  function preset(min: number): void {
    const end = new Date();
    const start = new Date(end.getTime() - min * 60_000);
    startVal = toLocal(start);
    endVal = toLocal(end);
  }
  preset(10);

  onMount(async () => {
    try {
      const hours = await listArchive(streamName);
      if (hours.length > 0) {
        const from = hours[0].start_ms;
        const to = hours[hours.length - 1].end_ms;
        const bytes = hours.reduce((a, h) => a + h.bytes, 0);
        available = { from, to, gb: bytes / 1024 ** 3 };
      }
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  });

  async function download(e: Event): Promise<void> {
    e.preventDefault();
    err = '';
    hint = '';
    if (!startVal || !endVal) {
      err = 'Заполни начало и конец';
      return;
    }
    const from = new Date(startVal).getTime();
    const to = new Date(endVal).getTime();
    if (!(to > from)) {
      err = 'Конец должен быть позже начала';
      return;
    }
    if ((to - from) / 1000 > 3600) {
      err = 'Максимум час за раз';
      return;
    }
    if (available && (to < available.from || from > available.to)) {
      err = 'За этот период записи нет';
      return;
    }
    if (h264 && to - from > 1_800_000) {
      err = 'С перекодировкой — не длиннее 30 минут. Для длинного фрагмента снимите галочку.';
      return;
    }

    // Сервер готовит файл в фоне: держать запрос открытым всё время
    // перекодировки нельзя — браузер его бросит, а файл придёт обрезанным.
    busy = true;
    const started = Date.now();
    try {
      for (;;) {
        const state = await clipState(streamName, from, to, h264);
        if (state === 'ready') break;
        const sec = Math.round((Date.now() - started) / 1000);
        hint = h264
          ? `Перекодирую в H.264… ${sec} с`
          : `Готовлю фрагмент… ${sec} с`;
        await new Promise((r) => setTimeout(r, 2000));
      }
      hint = 'Готово — скачивание началось';
      window.location.href = clipUrl(streamName, from, to, h264);
    } catch (e2) {
      err = e2 instanceof Error ? e2.message : String(e2);
      hint = '';
    } finally {
      busy = false;
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') onclose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="bg" onclick={(e) => { if (e.target === e.currentTarget) onclose(); }} role="presentation">
  <form class="modal" onsubmit={download}>
    <h2>Скачать фрагмент</h2>
    <p class="muted">{streamLabel}</p>

    {#if loading}
      <p class="muted">Смотрю, что записано…</p>
    {:else if available}
      <p class="range">Записано: {fmt(available.from)} — {fmt(available.to)} ({available.gb.toFixed(1)} ГБ)</p>
    {:else}
      <p class="range warn">Записи по этой камере пока нет</p>
    {/if}

    <div class="grid2">
      <label>Начало<input type="datetime-local" bind:value={startVal} required /></label>
      <label>Конец<input type="datetime-local" bind:value={endVal} required /></label>
    </div>
    <div class="presets">
      <button type="button" class="ghost" onclick={() => preset(5)}>−5 мин</button>
      <button type="button" class="ghost" onclick={() => preset(10)}>−10 мин</button>
      <button type="button" class="ghost" onclick={() => preset(30)}>−30 мин</button>
      <button type="button" class="ghost" onclick={() => preset(60)}>−1 час</button>
    </div>

    <label class="check">
      <input type="checkbox" bind:checked={h264} />
      <span>Перекодировать в H.264 — только если файл не открылся (не длиннее 30 минут)</span>
    </label>

    <div class="err">{err}</div>
    {#if hint}<div class="hint">{hint}</div>{/if}
    <div class="actions">
      <button type="button" class="ghost" onclick={onclose}>Закрыть</button>
      <button type="submit" class="primary" disabled={busy}>
        {busy ? 'Готовлю…' : 'Скачать'}
      </button>
    </div>
  </form>
</div>

<style>
  .bg {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(0,0,0,.45);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
  }
  .modal {
    width: min(460px, calc(100vw - 32px));
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 24px;
    display: flex; flex-direction: column; gap: 14px;
  }
  h2 { font-size: 17px; font-weight: 600; letter-spacing: -.01em; margin: 0; }
  .muted { color: var(--fg-muted); margin: 0; font-size: 13px; }
  .range { margin: 0; font-size: 12px; color: var(--fg-muted); }
  .range.warn { color: var(--bad); }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--fg-muted); font-weight: 500; }
  label.check { flex-direction: row; align-items: flex-start; gap: 8px; line-height: 1.35; }
  label.check input { margin-top: 1px; }
  input { padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-elev); color: var(--fg); }
  input:focus { outline: 2px solid var(--accent); outline-offset: -1px; border-color: var(--accent); }
  .presets { display: flex; gap: 6px; flex-wrap: wrap; }
  .err { color: var(--bad); font-size: 13px; min-height: 1.2em; }
  .hint { color: var(--fg-muted); font-size: 12px; }
  .actions { display: flex; gap: 10px; justify-content: flex-end; }
  button.primary { background: var(--accent); color: #fff; padding: 8px 14px; border-radius: 8px; font-weight: 500; }
  button.primary:hover { filter: brightness(1.08); }
  button.ghost { color: var(--fg-muted); padding: 8px 14px; border-radius: 8px; font-weight: 500; }
  button.ghost:hover { color: var(--fg); }
</style>
